import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NgApexchartsModule
} from "ng-apexcharts";
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { switchMap } from 'rxjs';
import { Strategy } from '../../interfaces/models/strategy.model';
import { DashboardService } from '../../services/dashboard.service';
import { SocketService } from '../../services/socket.service';
import { StrategyService } from '../../services/strategy.service';
import { TranslateModule } from '@ngx-translate/core';
import { DirectionService } from '../../services/direction.service';
import { CommonModule } from '@angular/common';
import { ChartsService } from '../../services/charts.service';

import { NavTitlePipe } from '../../pipes/nav-title.pipe';
import { filterActive } from '../../utils/filter-active';

@Component({
  selector: 'app-indicators-charts',
  standalone: true,
  imports: [
    NavTitlePipe,DropdownModule, DialogModule, CommonModule, FormsModule, NgApexchartsModule, TranslateModule],
  templateUrl: './indicators-charts.component.html',
  styleUrl: './indicators-charts.component.scss'
})
export class IndicatorsChartsComponent implements OnInit {

   
  strategyId = 0;
  selectedGoal = {} as any;
  strategyOptions = [] as any[]
  goalsOptions = [] as { goal_name: string, goal_id: number }[]
  indicatorOptions = [] as { indicator_name: string, indicator_id: number }[]
  goal_id = 0;
  selectedIndicatorId: number = 0;
  selectedIndicator: any = null;
  dir!: 'rtl' | 'ltr';
  goals: any[] = [];
  indicators: any[] = [];

  dualChart: any = null;
  singleChart: any = null;
  customLegendItems!: string[];
  indicatorModalVisible = false;
  
  constructor(
    private strategyService: StrategyService,
    private dashboardService: DashboardService,
    private socketService: SocketService,
    private directionService: DirectionService,
    private chartsService: ChartsService
  ) {

  }

  private setGoalsFromResponse(goals: any[]): void {
    this.goals = filterActive(goals).map((goal: any) => ({
      ...goal,
      indicators: filterActive(goal?.indicators)
    }));
    this.goalsOptions = this.goals.map((goal) => ({ goal_name: goal.name, goal_id: goal.id }));

    if (this.goalsOptions.length > 0) {
      this.goal_id = this.goalsOptions[0].goal_id;
      this.onGoalChange();
    } else {
      this.goal_id = 0;
      this.indicators = [];
      this.indicatorOptions = [];
    }
  }

  ngOnInit(): void {
    this.directionService.direction$.subscribe((dir) => {
      this.dir = dir;
      this.setCustomLegendItems();
    })

    this.strategyService.RawStrategies$.subscribe((strategies: Strategy[]) => {
      this.strategyOptions = filterActive(strategies).map((x) => ({ name: x.name, strategyId: x.id }))
    })

    this.strategyService.defaultStrategy$.pipe(switchMap((strategy: any) => {
      this.strategyId = strategy?.id;
      return this.dashboardService.getIndicatorsCharts(strategy.id)
    })).subscribe((response: any) => {
      this.setGoalsFromResponse(response['data']['goals']);
    });

    this.strategyService.selectedStrategy$.subscribe((strategy: any) => {
      this.strategyId = strategy?.id;
      this.dashboardService.getIndicatorsCharts(strategy.id).subscribe((response: any) => {
        this.setGoalsFromResponse(response['data']['goals']);
      })
    });
  }

  strategyOptionsChange() {
    this.strategyService.setStrategyIdfn(this.strategyId);
    this.socketService.setRefreshNotification(this.strategyId);
  }

  onGoalChange() {
    this.selectedIndicatorId = 0;
    this.selectedIndicator = null;
    this.indicatorOptions = [];

    if (!this.goal_id) {
      this.indicators = [];
      return;
    }

    const selectedGoal = this.goals.find(x => x.id == this.goal_id);
    if (!selectedGoal) {
      this.indicators = [];
      return;
    }

    // Show all indicators for selected goal
    this.indicators = selectedGoal['indicators'] || [];
    
    // Add calculated fields for display and pre-calculate chart options
    this.indicators = this.indicators.map((ind: any) => ({
      ...ind,
      performance: this.calculatePerformance(ind),
      trend: this.calculateTrend(ind),
      dualChartOptions: this.hasIndicatorData(ind) ? this.getIndicatorChartOptions(ind) : null
    }));
  }

  setCustomLegendItems() {
    this.customLegendItems = this.dir === 'rtl' ?
      ["المستهدف", "المحقق"] :
      ["Target", "Actual"];
    return this.customLegendItems;
  }
  
  // Calculate performance percentage
  calculatePerformance(indicator: any): number {
    const outcomes = indicator.mob_charts?.data || [];
    if (outcomes.length === 0) return 0;
    
    const totalTarget = outcomes.reduce((sum: number, outcome: any) => sum + (outcome.target || 0), 0);
    const totalActual = outcomes.reduce((sum: number, outcome: any) => sum + (outcome.actual || 0), 0);
    
    if (totalTarget === 0) return 0;
    return Math.min(100, Math.round((totalActual / totalTarget) * 100));
  }
  
  // Calculate trend based on actual data (comparing last two periods)
  calculateTrend(indicator: any): number {
    const outcomes = indicator.mob_charts?.data || [];
    if (outcomes.length < 2) return 0;
    
    // Get last two periods
    const lastPeriod = outcomes[outcomes.length - 1];
    const previousPeriod = outcomes[outcomes.length - 2];
    
    const lastPerformance = lastPeriod.target > 0 
      ? (lastPeriod.actual / lastPeriod.target) * 100 
      : 0;
    const previousPerformance = previousPeriod.target > 0 
      ? (previousPeriod.actual / previousPeriod.target) * 100 
      : 0;
    
    // Calculate change percentage
    if (previousPerformance === 0) return 0;
    const change = lastPerformance - previousPerformance;
    
    return Math.round(change * 10) / 10; // Round to 1 decimal place
  }
  
  // Get performance color
  getPerformanceColor(performance: number, opacity: number = 1): string {
    if (performance >= 90) return opacity < 1 ? `rgba(16, 185, 129, ${opacity})` : '#10b981'; // Green
    if (performance >= 75) return opacity < 1 ? `rgba(224, 168, 0, ${opacity})` : '#E0A800'; // Gold
    if (performance >= 50) return opacity < 1 ? `rgba(107, 114, 128, ${opacity})` : '#6b7280'; // Grey
    return opacity < 1 ? `rgba(239, 68, 68, ${opacity})` : '#ef4444'; // Red
  }
  
  // Get performance status text
  getPerformanceStatus(performance: number): string {
    if (performance >= 90) return this.dir === 'rtl' ? 'ممتاز' : 'Excellent';
    if (performance >= 75) return this.dir === 'rtl' ? 'جيد' : 'Good';
    if (performance >= 50) return this.dir === 'rtl' ? 'متوسط' : 'Average';
    return this.dir === 'rtl' ? 'مهدد' : 'At Risk';
  }

  // Generate chart configuration for indicator
  getIndicatorChartOptions(indicator: any): any {
    const outcomes = indicator.mob_charts?.data || [];
    const categories = outcomes.map((o: any) => o.period_name || '');
    const targetData = outcomes.map((o: any) => (o.target || 0).toFixed(2));
    const actualData = outcomes.map((o: any) => (o.actual || 0).toFixed(2));
    
    return {
      series: [
        {
          name: this.dir === 'rtl' ? 'المستهدف' : 'Target',
          data: targetData
        },
        {
          name: this.dir === 'rtl' ? 'المحقق' : 'Actual',
          data: actualData
        }
      ],
      chart: {
        type: 'bar',
        height: 280,
        fontFamily: 'Cairo, sans-serif',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 6,
          borderRadiusApplication: 'end'
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontFamily: 'Cairo, sans-serif',
            fontSize: '12px',
            fontWeight: 600,
            colors: '#546e7a'
          }
        }
      },
      yaxis: {
        title: {
          text: '',
          style: {
            fontFamily: 'Cairo, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
            color: '#546e7a'
          }
        },
        labels: {
          style: {
            fontFamily: 'Cairo, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            colors: '#546e7a'
          },
          formatter: (value: number) => {
            return value.toFixed(0) + '%';
          }
        }
      },
      colors: ['#C91F3B', '#546e7a'],
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        fontFamily: 'Cairo, sans-serif',
        fontSize: '14px',
        fontWeight: 600,
        markers: {
          width: 12,
          height: 12,
          radius: 3
        },
        itemMargin: {
          horizontal: 15,
          vertical: 10
        }
      },
      tooltip: {
        y: {
          formatter: (val: number) => {
            return val.toFixed(2) + '%';
          }
        },
        style: {
          fontFamily: 'Cairo, sans-serif',
          fontSize: '13px'
        }
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 0,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      }
    };
  }

  // Check if indicator has data
  hasIndicatorData(indicator: any): boolean {
    const outcomes = indicator.mob_charts?.data || [];
    return outcomes.length > 0;
  }

}
